var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, Box, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import CommentArea from "../common/CommentArea";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { getElementValueKey } from "../../../../utils/helpers";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
var MATRIX_DEFAULT_ROW_NAME = "default";
var reorderRows = function (rows, orderBy) {
    var _a;
    var rowsFormatted = rows.map(function (row) {
        if (typeof row === "string") {
            return { value: row, text: row };
        }
        return row;
    });
    if (orderBy === "random") {
        var shuffled = __spreadArray([], rowsFormatted, true);
        for (var i = shuffled.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            _a = [shuffled[j], shuffled[i]], shuffled[i] = _a[0], shuffled[j] = _a[1];
        }
        return shuffled;
    }
    return rowsFormatted;
};
var MatrixField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var unregister = useFormContext().unregister;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _b = useElementConditions(element), isVisible = _b.isVisible, isEnabled = _b.isEnabled, isInteractive = _b.isInteractive, fieldRequired = _b.isRequired;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var rows = useMemo(function () {
        return (element.rows || []);
    }, [element.rows]);
    var columns = useMemo(function () {
        return (element.columns || []);
    }, [element.columns]);
    var orderedRows = useMemo(function () {
        if (element.rowsOrder) {
            return reorderRows(rows, element.rowsOrder);
        }
        return rows;
    }, [element.rowsOrder, rows]);
    var initialValue = getInitialValueForElement(element);
    var hasCellText = useMemo(function () { return Object.keys((element === null || element === void 0 ? void 0 : element.cells) || {}).length > 0; }, [element === null || element === void 0 ? void 0 : element.cells]);
    var getCellDisplayText = function (rowValue, columnValue, columnText) {
        var _a, _b;
        var rowCells = (_a = element === null || element === void 0 ? void 0 : element.cells) === null || _a === void 0 ? void 0 : _a[rowValue];
        if (rowCells && rowCells[columnValue]) {
            return rowCells[columnValue];
        }
        var defaultCells = (_b = element === null || element === void 0 ? void 0 : element.cells) === null || _b === void 0 ? void 0 : _b[MATRIX_DEFAULT_ROW_NAME];
        if (defaultCells && defaultCells[columnValue]) {
            return defaultCells[columnValue];
        }
        return columnText || columnValue;
    };
    var handleCellClick = function (row, column, currentValues, onChange) {
        var _a;
        if (!isInteractive)
            return;
        onChange(__assign(__assign({}, currentValues), (_a = {}, _a[row] = column, _a)));
    };
    if (!element.rows && element.hideIfRowsEmpty) {
        return null;
    }
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var matrixValues = ((_b = field.value) !== null && _b !== void 0 ? _b : {});
                            return (_jsxs(Box, { children: [_jsx(TableContainer, __assign({ sx: {
                                            boxShadow: "none",
                                            backgroundColor: "transparent",
                                        } }, { children: _jsxs(Table, __assign({ size: "small" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                                    fontWeight: 600,
                                                                } }), columns.map(function (column, index) { return (_jsx(TableCell, __assign({ align: "center", sx: {
                                                                    fontWeight: 600,
                                                                    minWidth: 120,
                                                                } }, { children: typeof column === "string"
                                                                    ? column
                                                                    : column.text }), index)); })] }) }), _jsx(TableBody, { children: orderedRows.map(function (row, rowIndex) {
                                                        var rowText = typeof row === "string" ? row : row.text;
                                                        return (_jsxs(TableRow, __assign({ role: hasCellText ? "radiogroup" : undefined, "aria-label": hasCellText ? rowText : undefined }, { children: [_jsx(TableCell, __assign({ component: "th", scope: "row", sx: {
                                                                        fontWeight: 500,
                                                                    } }, { children: rowText })), columns.map(function (column, colIndex) {
                                                                    var columnValue = typeof column === "string"
                                                                        ? column
                                                                        : column.value;
                                                                    var rowValue = typeof row === "string" ? row : row.value;
                                                                    var isSelected = matrixValues[rowValue] === columnValue;
                                                                    if (hasCellText) {
                                                                        return (_jsx(TableCell, __assign({ align: "center", role: "radio", "aria-checked": isSelected, "aria-disabled": !isInteractive, tabIndex: isInteractive ? 0 : -1, onKeyDown: function (event) {
                                                                                if (event.key !== " " &&
                                                                                    event.key !== "Enter") {
                                                                                    return;
                                                                                }
                                                                                event.preventDefault();
                                                                                handleCellClick(rowValue, columnValue, matrixValues, field.onChange);
                                                                            }, sx: {
                                                                                cursor: !isInteractive
                                                                                    ? "default"
                                                                                    : "pointer",
                                                                                fontWeight: isSelected ? 600 : 400,
                                                                                color: isSelected
                                                                                    ? "primary.contrastText"
                                                                                    : "text.primary",
                                                                                bgcolor: isSelected
                                                                                    ? "primary.main"
                                                                                    : "transparent",
                                                                                "&:hover": {
                                                                                    bgcolor: !isInteractive
                                                                                        ? isSelected
                                                                                            ? "primary.main"
                                                                                            : "transparent"
                                                                                        : isSelected
                                                                                            ? "primary.dark"
                                                                                            : "action.hover",
                                                                                },
                                                                            }, onClick: function () {
                                                                                return handleCellClick(rowValue, columnValue, matrixValues, field.onChange);
                                                                            } }, { children: getCellDisplayText(rowValue, columnValue, typeof column === "string"
                                                                                ? column
                                                                                : column.text) }), colIndex));
                                                                    }
                                                                    return (_jsx(TableCell, __assign({ align: "center", sx: {
                                                                            cursor: !isInteractive
                                                                                ? "default"
                                                                                : "pointer",
                                                                            "&:hover": {
                                                                                bgcolor: !isInteractive
                                                                                    ? "transparent"
                                                                                    : "action.hover",
                                                                            },
                                                                        }, onClick: function () {
                                                                            return isInteractive &&
                                                                                handleCellClick(rowValue, columnValue, matrixValues, field.onChange);
                                                                        } }, { children: _jsx(Radio, { checked: isSelected, disabled: !isInteractive, value: columnValue, size: "small", sx: { pointerEvents: "none" } }) }), colIndex));
                                                                })] }), rowIndex));
                                                    }) })] })) })), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default MatrixField;
